// Default line catalog: the lines anyone can practice, with their pre-generated position trees.
// Both are static JSON under /catalog, produced by scripts/catalog/build-trees.mjs.

import type { Tree } from "../domain/catalogTree.ts";
import type { CatalogBand } from "../domain/opponentLabel.ts";
import type { LevelReference } from "../domain/levelMetrics.ts";

export type LineColor = "white" | "black";
export type LineLevel = "beginner" | "club" | "advanced";

/** A line of the catalog, exactly as the server serves it in /catalog/index.json. */
export interface CatalogLine {
    slug: string;
    name: string;
    family: string;
    eco: string;
    color: LineColor;
    level: LineLevel;
    moves: string;   // "e4 c5 Nf3 d6"
    uci: string;     // "e2e4 c7c5 g1f3 d7d6"
    idea: string;
}

export interface CatalogIndex {
    v?: number;
    generatedAt: string;
    /** The rating bands the trees were generated at, by key: { club: { label, ratings, speeds } }. */
    bands?: Record<string, CatalogBand>;
    lines: CatalogLine[];
}

const INDEX_URL = "/catalog/index.json";
const DEFAULT_BAND = "club";

/** The band every catalog practice is answered from. The header names it, so it is not a secret. */
export const PRACTICE_BAND = DEFAULT_BAND;

let indexPromise: Promise<CatalogIndex> | null = null;
let loadedIndex: CatalogIndex | null = null;
const treePromises = new Map<string, Promise<Tree>>();

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(url, options);
    if (!response.ok) {
        throw new Error(`Failed to load ${url}: HTTP ${response.status}`);
    }
    return response.json() as Promise<T>;
}

let levelReferencePromise: Promise<LevelReference> | null = null;

/** What the level metrics compare a player with (scripts/reference/levels.mjs). Revalidated, like the index. */
export function loadLevelReference(): Promise<LevelReference> {
    if (!levelReferencePromise) {
        levelReferencePromise = fetchJson<LevelReference>("/catalog/level-reference.json", { cache: "no-cache" });
        levelReferencePromise.catch(() => {
            levelReferencePromise = null;
        });
    }
    return levelReferencePromise;
}

/** Catalog index: { v, bands, lines: [{slug, name, family, eco, color, level, moves, uci, idea}] } */
export function loadIndex(): Promise<CatalogIndex> {
    if (!indexPromise) {
        // Always revalidated: static files are cached for a year in production. The trees are versioned
        // by the generation date instead.
        indexPromise = fetchJson<CatalogIndex>(INDEX_URL, { cache: "no-cache" })
            .then((index) => {
                loadedIndex = index;
                return index;
            })
            .catch((err) => {
                indexPromise = null;
                throw err;
            });
    }
    return indexPromise;
}

/**
 * A band of the index, for whoever has to name the opponent while painting. Null until the index is
 * loaded, which it always is by the time a catalog line is on the board: its tree waits for it.
 */
export function bandOf(band: string = DEFAULT_BAND): CatalogBand | null {
    return loadedIndex?.bands?.[band] ?? null;
}

export async function getLines(): Promise<CatalogLine[]> {
    return (await loadIndex()).lines;
}

export async function findLine(slug: string): Promise<CatalogLine | null> {
    return (await getLines()).find((line) => line.slug === slug) || null;
}

/** Position tree of a line. Cached for the session: a practice is usually repeated. */
export function loadTree(slug: string, band: string = DEFAULT_BAND): Promise<Tree> {
    const key = `${band}/${slug}`;
    if (!treePromises.has(key)) {
        const promise = loadIndex()
            .then((index) => fetchJson<Tree>(`/catalog/trees/${key}.json?v=${index.generatedAt}`))
            .catch((err) => {
                treePromises.delete(key);
                throw err;
            });
        treePromises.set(key, promise);
    }
    return treePromises.get(key)!;
}

/**
 * Random line, avoiding the ones played most recently so "Another line" always feels new.
 * A first-time visitor is not thrown into the Najdorf: until they have practiced, advanced lines wait.
 */
export async function pickRandomLine(recentSlugs: string[] = [],
                                     { newcomer = false, random = Math.random } = {}): Promise<CatalogLine | null> {
    const lines = await getLines();
    const fresh = lines.filter((line) => !recentSlugs.includes(line.slug));
    const gentle = newcomer ? fresh.filter((line) => line.level !== "advanced") : fresh;
    const pool = gentle.length ? gentle : fresh.length ? fresh : lines;
    return pool[Math.floor(random() * pool.length)] || null;
}

/**
 * "e4 c5 Nf3" -> "1.e4 c5 2.Nf3". The rule moved to the domain, where the position of a line is
 * worked out too; it is still exported from here because this is where a caller looks for it.
 */
export { numberedMoves } from "../domain/linePosition.ts";
